import React, { useState, useRef, useEffect } from 'react';

import { Button, Checkbox, RotatingChevron } from '@newsela/angelou';
import PropTypes from 'prop-types';

import { $customSelectContainer, $chevronDown, $button, $options, $customSelectLabel, $buttonsContainer, $checkbox, $clearButton } from './style';

export default function CustomSelect ({
  name,
  applyLabel,
  options,
  value = [],
  onChange,
  placeholder = 'Select...',
  customDropdownBody,
  pendingValue,
  setPendingValue,
  getLabel,
  hasError = false,
  errorMessage = null

}) {
  const [open, setOpen] = useState(false);
  // For checkboxes, use internal pendingSelection. For custom, use parent's state, e.g., pendingValue/setPendingValue.
  const [pendingSelection, setPendingSelection] = useState(value || []);
  const dropdownRef = useRef(null);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleCheckboxChange = (optionValue) => {
    setPendingSelection((prev) =>
      prev.includes(optionValue)
        ? prev.filter((v) => v !== optionValue)
        : [...prev, optionValue]
    );
  };

  const handleApply = () => {
    if (customDropdownBody && pendingValue !== undefined) {
      onChange(pendingValue);
    } else {
      onChange(pendingSelection);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setPendingSelection([]);
    if (setPendingValue) setPendingValue([]);
  };

  const defaultLabel = () => {
    if (!value || !value.length) return placeholder;
    // Not truly generic, but works for the range case.
    if (customDropdownBody) {
      if (typeof value === 'object' && value.from !== undefined && value.to !== undefined) {
        return value.from && value.to ? `${value.from} - ${value.to}` : placeholder;
      }
    }
    const selectedLabels = options
      .filter((opt) => value.includes(opt.value))
      .map((opt) => opt.label);
    return selectedLabels.join(', ');
  };

  const label = getLabel ? getLabel() : defaultLabel();

  useEffect(() => {
    setPendingSelection(value || []);
    if (setPendingValue) setPendingValue(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside (event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setPendingSelection(value || []);
        if (setPendingValue) setPendingValue(value);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, value, setPendingValue]);

  return (
    <div style={{ position: 'relative', minWidth: 200 }} ref={dropdownRef}>
      <div css={$customSelectLabel}>Filter by {name}</div>
      <div
        css={$customSelectContainer}
        onClick={handleToggle}
      >
        {label}
        <RotatingChevron
          isDown={!open}
          timeout={300}
          alt='Toggle dropdown'
          __cssFor={{ root: $chevronDown }}
        />
      </div>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '110%',
            left: 0,
            zIndex: 10,
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minWidth: 200,
            padding: '12px 8px 8px',
          }}
        >
          {customDropdownBody ? (
            customDropdownBody({
              pendingValue,
              setPendingValue,
              handleCancel,
              handleApply,
              hasError,
              errorMessage,
            })
          ) : (
            <div css={$options}>
              {options.map((opt) => (
                <Checkbox
                  key={opt.value}
                  checked={pendingSelection.includes(opt.value)}
                  onChange={() => handleCheckboxChange(opt.value)}
                  label={opt.label}
                  __cssFor={{ root: $checkbox }}
                />
              ))}
            </div>
          )}
          <div css={$buttonsContainer}>
            <Button flavor='SECONDARY' onClick={handleCancel} __cssFor={{ root: $clearButton }}>
              Clear filter
            </Button>
            <Button
              __cssFor={{ root: $button }}
              onClick={handleApply}
              isFullWidth
              disabled={hasError}
            >
              {applyLabel || `Apply ${name}s`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

CustomSelect.propTypes = {
  name: PropTypes.string,
  applyLabel: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  value: PropTypes.any, // array for checkboxes, object for range
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  customDropdownBody: PropTypes.func,
  pendingValue: PropTypes.any,
  setPendingValue: PropTypes.func,
  getLabel: PropTypes.func,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
};
